/**
 * E-mail sign-in of the game's accounts (feature 'email'; the address rules: src/shared/email-rules.ts).
 *
 * The nickname stays the account's name; an e-mail address is a second way to sign in and the way back in after a
 * forgotten password. An address belongs to an account only once its owner opened the confirmation link:
 *  - auth.emailSignUp (a nickname account like auth.signUp, plus an address) and auth.emailChange (an account adds or
 *    changes its address, confirmed with its password) make the address PENDING and mail a link to it
 *    (https://<site>/link/?a=verify#t=<token>, config.email.verifyTtlMs). A pending address blocks nobody: anybody may
 *    ask for it, only the inbox's owner can confirm it.
 *  - auth.emailVerify (the page public/link/ or the app, through Android App Links) confirms it: the index C.emails
 *    (address key -> account) gets it, the account's previous address goes.
 *  - auth.emailSignIn signs in with a confirmed address and the password (AuthService.signInByEmail: the answers,
 *    limits and timing of auth.signIn).
 *  - auth.passwordResetRequest mails a reset link (?a=reset, config.email.resetTtlMs, single use) to a confirmed
 *    address of an account with a password; auth.passwordReset sets the new password and ends every session. It is
 *    the account's recovery, so it also voids what somebody who knew the old password could have left behind: the
 *    pending address and its confirmation link (an address change to that somebody's inbox), and the account's link
 *    to a Play Games player (the `recovered` hook: services/play-games.ts).
 *
 * No account enumeration: no answer depends on whether an address belongs to an account. A sign-up or a change to an
 * address that another account has confirmed answers like any other (that address's owner gets a notice instead of a
 * link); a reset request answers { accepted: true } at once and does the lookup and the mail afterwards; a sign-in
 * with an unknown address is a wrong password.
 *
 * Tokens: 32 random bytes (base64url) in the link's fragment (never sent to a web server, never in an access log);
 * the server keeps only their keyed hash (AuthService.digest, as every token of the backend), each with its kind,
 * account, address and expiry; a token works once, a newer link of the same kind replaces the older ones, a changed
 * sign-in address voids the reset links of the old one.
 *
 * Limits (memory, sliding hour; a restart forgets them): mails one account causes (config.email.sendsPerAccountPerHour:
 * RATE_LIMITED 'email_send_limit'), mails one address receives (sendsPerAddressPerHour: silently skipped, so the
 * answer tells nothing), reset requests per client address (requestsPerClientPerHour: RATE_LIMITED). The security
 * notices (SECURITY_NOTICES) have a budget of their own per address (noticesPerAddressPerHour): mails anybody can
 * cause to an address never suppress them. Wrong passwords count like the sign-in's.
 *
 * Every step is in the account audit (./account-audit.ts: addresses only as tags). The mail itself goes through an
 * EmailSender (src/server/node/email-senders.ts: the development console, SMTP, an HTTP mail API); a failed send is
 * logged and audited, never retried by itself (the player asks again). Locks: 'email:<key>' before 'emailp:<playerId>'.
 */
import { checkPassword } from '../../../shared/auth-rules';
import { checkEmail, emailKey, maskEmail } from '../../../shared/email-rules';
import { BackendError } from '../../../shared/protocol/errors';
import type { DeviceInfo, EmailStatusResult, EmailVerifiedResult, IssuedAuthResult, MessageLanguage, PlayerId } from '../../../shared/protocol/model';
import { randomToken } from '../../../shared/random';
import { sha256Hex } from '../../../shared/crypto/sha256';
import { addressLimitKey } from '../client-address';
import { type EmailKind, type EmailVars, renderEmail } from '../email-templates';
import { FailureCounter } from '../failure-counter';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import type { AccountAudit } from './account-audit';
import { type AuthOutcome, type AuthService, type SignInMeta, credentialProblemError } from './auth';
import { C, type AccountRecord, type ServiceDeps } from './deps';

/** One e-mail of the game (services/email.ts renders it, an EmailSender delivers it). */
export interface EmailMessage {
  to: string;
  subject: string;
  text: string;
  html: string;
  kind: EmailKind;
  /** The account link in it (null: a notice without a link); the development console prints it. */
  link: string | null;
}

/** Delivers e-mails (src/server/node/email-senders.ts). Throws when the mail could not be handed over. */
export interface EmailSender {
  /** 'console', 'smtp', 'resend', 'postmark', 'sendgrid' (logs, /health). */
  readonly provider: string;
  send(message: EmailMessage): Promise<void>;
}

/**
 * A confirmed sign-in address (C.emails, id = emailIndexId of the address: a fixed-length SHA-256, so an address of
 * any length and characters makes a valid document id and no address appears in a file name of DATA_DIR).
 */
export interface EmailIndexRecord {
  email: string;
  playerId: PlayerId;
  verifiedAt: number;
}

/** An open link (C.emailTokens, id = keyed hash of its token). */
export interface EmailTokenRecord {
  hash: string;
  kind: 'verify' | 'reset';
  playerId: PlayerId;
  /** verify: the address it confirms; reset: the sign-in address it was sent to. */
  email: string;
  createdAt: number;
  expiresAt: number;
}

/** The e-mail state of an account (C.emailAccounts, id = playerId). */
export interface EmailAccountRecord {
  playerId: PlayerId;
  /** The confirmed sign-in address (null: none). */
  email: string | null;
  verifiedAt: number | null;
  pending: { email: string; at: number } | null;
  /** The account's open links (their records are in C.emailTokens). */
  tokens: Array<{ hash: string; kind: 'verify' | 'reset'; expiresAt: number }>;
  /** The language of the account's last e-mail request (notices to it use it). */
  lang: MessageLanguage | null;
}

/** The document id of an address in C.emails (stable across AUTH_SECRET rotations: unkeyed). */
export function emailIndexId(key: string): string {
  return sha256Hex(`fwp-email-index:${key}`);
}

/** The path of the account links on the site (public/link/; the app's App Links filter, AndroidManifest.xml). */
export const ACCOUNT_LINK_PATH = '/link/';

/** The account link of a token: the token is in the fragment (see the module comment). */
export function accountLink(base: string, kind: 'verify' | 'reset', token: string): string {
  return `${base.replace(/\/+$/, '')}${ACCOUNT_LINK_PATH}?a=${kind}#t=${token}`;
}

const HOUR_MS = 3600_000;

/**
 * Notices to an account's own confirmed address about a change to it, sent only after the account's password or one
 * of its reset links was used (so nobody else can cause them): counted apart from the other mails of the address.
 */
const SECURITY_NOTICES: ReadonlySet<EmailKind> = new Set<EmailKind>(['email_changing', 'password_changed']);

function emailProblem(reason: 'email_required' | 'email_too_long' | 'email_invalid'): BackendError {
  const message = reason === 'email_required' ? 'Enter an e-mail address' : reason === 'email_too_long' ? 'The e-mail address is too long' : 'This is not an e-mail address';
  return new BackendError('VALIDATION_FAILED', message, { reason, field: 'email' });
}

function tokenInvalid(): BackendError {
  return new BackendError('VALIDATION_FAILED', 'This link is invalid or expired: ask for a new one', { reason: 'email_token_invalid' });
}

function sendLimit(retryAfterMs: number): BackendError {
  return new BackendError('RATE_LIMITED', 'Too many e-mails were asked for: try again later', { reason: 'email_send_limit', retryAfterMs });
}

export class EmailService {
  /**
   * Mails per account ('p:'), per receiving address ('m:'; its security notices: 's:') and reset requests per client
   * address ('c:'), last hour.
   */
  private readonly sends = new FailureCounter(HOUR_MS, 50_000);
  /** Deliveries under way (idle() waits for them). */
  private readonly deliveries = new Set<Promise<void>>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly audit: AccountAudit,
    private readonly sender: EmailSender | null,
    /**
     * After a password reset (the account's recovery): drops the account's other ways in that do not depend on the
     * password (backend.ts: the Play Games link); returns what went, for the audit.
     */
    private readonly recovered: (playerId: PlayerId) => Promise<{ playGamesUnlinked: boolean }> = async () => ({ playGamesUnlinked: false }),
  ) {}

  /** The feature is on: a sender, password accounts and the site of the links. */
  get enabled(): boolean {
    return this.sender !== null && this.auth.credentialsEnabled && !!this.d.config.email.linkBaseUrl;
  }

  /** The mail provider ('console', 'smtp', ...) or null. */
  get provider(): string | null {
    return this.sender?.provider ?? null;
  }

  /** Resolves when every delivery under way is done (tests, shutdown). */
  async idle(): Promise<void> {
    while (this.deliveries.size > 0) await Promise.allSettled([...this.deliveries]);
  }

  /** A typed address, checked: VALIDATION_FAILED { reason, field: 'email' } when the rules refuse it. */
  checkAddress(raw: string): { email: string; key: string } {
    const c = checkEmail(raw);
    if (!c.ok) throw emailProblem(c.reason);
    return { email: c.email, key: c.key };
  }

  // ------------------------------------------------------------------------------------------------ records

  private async loadDoc(playerId: PlayerId): Promise<EmailAccountRecord> {
    const doc = await this.d.storage.get<EmailAccountRecord>(C.emailAccounts, playerId);
    return doc ?? { playerId, email: null, verifiedAt: null, pending: null, tokens: [], lang: null };
  }

  private tokenHash(token: string): string {
    return this.auth.digest.hash(`email-link:${token}`);
  }

  /** The record of a link's token (current secret first, then the previous ones), or null. */
  private async findToken(token: string): Promise<EmailTokenRecord | null> {
    for (const hash of [this.tokenHash(token), ...this.auth.digest.fallbackHashes(`email-link:${token}`)]) {
      const rec = await this.d.storage.get<EmailTokenRecord>(C.emailTokens, hash);
      if (rec) return rec;
    }
    return null;
  }

  /** A new link of `kind`: the token, its record and the account's entry (older links of that kind go). */
  private newToken(doc: EmailAccountRecord, kind: 'verify' | 'reset', email: string, ttlMs: number): { token: string; ops: StorageBatchOp[] } {
    const now = this.d.clock.now();
    const token = randomToken(this.d.rng, 32);
    const hash = this.tokenHash(token);
    const ops: StorageBatchOp[] = doc.tokens.filter((t) => t.kind === kind).map((t) => deleteOp(C.emailTokens, t.hash));
    const rec: EmailTokenRecord = { hash, kind, playerId: doc.playerId, email, createdAt: now, expiresAt: now + ttlMs };
    doc.tokens = [...doc.tokens.filter((t) => t.kind !== kind && t.expiresAt > now), { hash, kind, expiresAt: rec.expiresAt }];
    ops.push(putOp(C.emailTokens, hash, rec));
    return { token, ops };
  }

  private nicknameOf(a: AccountRecord | null): string {
    return a?.nickname ?? a?.displayName ?? 'FWP';
  }

  status(doc: EmailAccountRecord): EmailStatusResult {
    return { available: this.enabled, email: doc.email, pendingEmail: doc.pending?.email ?? null };
  }

  async statusOf(playerId: PlayerId): Promise<EmailStatusResult> {
    return this.status(await this.loadDoc(playerId));
  }

  // ------------------------------------------------------------------------------------------------ limits

  /** RATE_LIMITED 'email_send_limit' when the key has used its budget, else counts one more. */
  private take(key: string, max: number): void {
    const now = this.d.clock.now();
    const retry = this.sends.retryAfter(key, max, now);
    if (retry !== null) throw sendLimit(retry);
    this.sends.note(key, now, max);
  }

  /** Counts a mail of `kind` to an address; false (skip it, silently) when the address got its hourly share of them. */
  private addressBudget(key: string, kind: EmailKind): boolean {
    const notice = SECURITY_NOTICES.has(kind);
    const max = notice ? this.d.config.email.noticesPerAddressPerHour : this.d.config.email.sendsPerAddressPerHour;
    const counter = `${notice ? 's' : 'm'}:${key}`;
    const now = this.d.clock.now();
    if (this.sends.retryAfter(counter, max, now) !== null) return false;
    this.sends.note(counter, now, max);
    return true;
  }

  // ------------------------------------------------------------------------------------------------ delivery

  /** Renders and sends a mail in the background (the answer never waits for, or depends on, the delivery). */
  private deliver(playerId: PlayerId | null, to: string, kind: EmailKind, lang: MessageLanguage | null, vars: EmailVars): void {
    const sender = this.sender;
    if (!sender) return;
    const key = emailKey(to);
    const tag = this.audit.addressTag(key);
    if (!this.addressBudget(key, kind)) {
      void this.audit.record({ event: 'email_skipped', playerId, ok: false, details: { kind, to: tag, why: 'address_limit' } });
      return;
    }
    const rendered = renderEmail(kind, lang ?? undefined, vars);
    const job = (async () => {
      try {
        await sender.send({ to, ...rendered, kind, link: vars.link ?? null });
        await this.audit.record({ event: 'email_sent', playerId, ok: true, details: { kind, to: tag, provider: sender.provider } });
      } catch (err) {
        this.d.logger.error('e-mail not sent', { kind, provider: sender.provider, err: err instanceof Error ? err.message.slice(0, 300) : String(err) });
        await this.audit.record({ event: 'email_sent', playerId, ok: false, details: { kind, to: tag, provider: sender.provider, error: 'send_failed' } });
      }
    })();
    this.deliveries.add(job);
    void job.finally(() => this.deliveries.delete(job));
  }

  /** Runs `fn` in the background after the answer (reset requests); its failure is logged. */
  private background(fn: () => Promise<void>): void {
    const job = fn().catch((err) => this.d.logger.error('e-mail request failed', { err }));
    this.deliveries.add(job);
    void job.finally(() => this.deliveries.delete(job));
  }

  // ------------------------------------------------------------------------------------------------ addresses

  /**
   * Makes `addr` the account's pending address and mails its confirmation link (or, when another account has
   * confirmed it, a notice to that address instead: the same answer). The account's own confirmed address again:
   * nothing pending, nothing sent. `cause` goes to the audit; 'change' also notifies the current address.
   */
  private async requestAddress(a: AccountRecord, addr: { email: string; key: string }, lang: MessageLanguage | undefined, cause: 'signup' | 'change' | 'resend'): Promise<EmailStatusResult> {
    const playerId = a.playerId;
    this.take(`p:${playerId}`, this.d.config.email.sendsPerAccountPerHour);
    const base = this.d.config.email.linkBaseUrl!;
    return this.d.mutex.run(`email:${addr.key}`, () =>
      this.d.mutex.run(`emailp:${playerId}`, async () => {
        const doc = await this.loadDoc(playerId);
        if (lang) doc.lang = lang;
        const ops: StorageBatchOp[] = [];
        if (doc.email && emailKey(doc.email) === addr.key) {
          // Already this account's address: whatever was pending is dropped.
          for (const t of doc.tokens.filter((x) => x.kind === 'verify')) ops.push(deleteOp(C.emailTokens, t.hash));
          doc.tokens = doc.tokens.filter((x) => x.kind !== 'verify');
          doc.pending = null;
          await this.d.storage.batch([...ops, putOp(C.emailAccounts, playerId, doc)]);
          return this.status(doc);
        }
        const holder = await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(addr.key));
        const inUse = holder !== null && holder.playerId !== playerId;
        const previous = doc.email;
        doc.pending = { email: addr.email, at: this.d.clock.now() };
        let link: string | null = null;
        if (inUse) {
          for (const t of doc.tokens.filter((x) => x.kind === 'verify')) ops.push(deleteOp(C.emailTokens, t.hash));
          doc.tokens = doc.tokens.filter((x) => x.kind !== 'verify');
        } else {
          const t = this.newToken(doc, 'verify', addr.email, this.d.config.email.verifyTtlMs);
          ops.push(...t.ops);
          link = accountLink(base, 'verify', t.token);
        }
        await this.d.storage.batch([...ops, putOp(C.emailAccounts, playerId, doc)]);
        const nickname = this.nicknameOf(a);
        if (inUse) this.deliver(holder.playerId, holder.email, 'address_in_use', doc.lang, {});
        else this.deliver(playerId, addr.email, 'verify', doc.lang, { nickname, link: link!, hours: Math.round(this.d.config.email.verifyTtlMs / HOUR_MS) });
        if (cause === 'change' && previous) this.deliver(playerId, previous, 'email_changing', doc.lang, { nickname, newEmail: maskEmail(addr.email) });
        await this.audit.record({ event: cause === 'signup' ? 'email_signup' : cause === 'change' ? 'email_change_requested' : 'email_resend', playerId, ok: true, details: { to: this.audit.addressTag(addr.key) } });
        return this.status(doc);
      }),
    );
  }

  /** auth.emailSignUp, after the account was created (the router signed it up like auth.signUp). */
  async afterSignUp(playerId: PlayerId, addr: { email: string; key: string }, lang: MessageLanguage | undefined): Promise<void> {
    const a = await this.auth.requireAccount(playerId);
    await this.requestAddress(a, addr, lang, 'signup');
  }

  /** auth.emailChange: the account's password confirms it (the sign-in's limits and refusal). */
  async change(playerId: PlayerId, p: { email: string; password: string; lang?: MessageLanguage }, meta?: SignInMeta): Promise<EmailStatusResult> {
    const addr = this.checkAddress(p.email);
    const a = await this.auth.requireAccount(playerId);
    if (a.passwordHash === null) throw new BackendError('FORBIDDEN', 'This account has no password: e-mail sign-in needs one', { reason: 'password_not_set' });
    await this.auth.confirmDeletion(a, p.password, meta);
    return this.requestAddress(a, addr, p.lang, 'change');
  }

  /** auth.emailResend: the pending address's link again (a new token; the old link stops working). */
  async resend(playerId: PlayerId, lang: MessageLanguage | undefined): Promise<EmailStatusResult> {
    const doc = await this.loadDoc(playerId);
    if (!doc.pending) throw new BackendError('INVALID_STATE', 'No e-mail address is waiting for its confirmation', { reason: 'email_not_pending' });
    const a = await this.auth.requireAccount(playerId);
    return this.requestAddress(a, { email: doc.pending.email, key: emailKey(doc.pending.email) }, lang ?? doc.lang ?? undefined, 'resend');
  }

  /** auth.emailVerify: the pending address of the link's account becomes its sign-in address. */
  async verify(token: string): Promise<EmailVerifiedResult> {
    const rec = await this.findToken(token);
    const now = this.d.clock.now();
    if (!rec || rec.kind !== 'verify' || rec.expiresAt <= now) {
      // A token nobody was given names no account: counted, not stored (anybody can send them at the request rate).
      if (!rec) this.audit.noteUnattributed('email_verify_refused');
      else await this.audit.record({ event: 'email_verify_refused', playerId: rec.playerId, ok: false, details: { why: rec.kind !== 'verify' ? 'wrong_kind' : 'expired' } });
      throw tokenInvalid();
    }
    const key = emailKey(rec.email);
    const playerId = rec.playerId;
    const outcome = await this.d.mutex.run(`email:${key}`, () =>
      this.d.mutex.run(`emailp:${playerId}`, async (): Promise<{ changed: boolean } | 'invalid' | 'taken'> => {
        const doc = await this.loadDoc(playerId);
        const live = doc.pending && emailKey(doc.pending.email) === key && doc.tokens.some((t) => t.hash === rec.hash);
        if (!live || !(await this.auth.getAccount(playerId))) {
          await this.d.storage.batch([deleteOp(C.emailTokens, rec.hash)]);
          return 'invalid';
        }
        const holder = await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(key));
        const verifyOps = doc.tokens.filter((t) => t.kind === 'verify').map((t) => deleteOp(C.emailTokens, t.hash));
        if (holder && holder.playerId !== playerId) {
          doc.pending = null;
          doc.tokens = doc.tokens.filter((t) => t.kind !== 'verify');
          await this.d.storage.batch([...verifyOps, putOp(C.emailAccounts, playerId, doc)]);
          return 'taken';
        }
        const ops: StorageBatchOp[] = [...verifyOps];
        const previous = doc.email;
        if (previous && emailKey(previous) !== key) {
          const old = await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(emailKey(previous)));
          if (old?.playerId === playerId) ops.push(deleteOp(C.emails, emailIndexId(emailKey(previous))));
          // Reset links went to the old address: they stop working with it.
          for (const t of doc.tokens.filter((x) => x.kind === 'reset')) ops.push(deleteOp(C.emailTokens, t.hash));
          doc.tokens = doc.tokens.filter((x) => x.kind !== 'reset');
        }
        ops.push(putOp(C.emails, emailIndexId(key), { email: rec.email, playerId, verifiedAt: now } satisfies EmailIndexRecord));
        doc.email = rec.email;
        doc.verifiedAt = now;
        doc.pending = null;
        doc.tokens = doc.tokens.filter((t) => t.kind !== 'verify');
        ops.push(putOp(C.emailAccounts, playerId, doc));
        await this.d.storage.batch(ops);
        return { changed: previous !== null && emailKey(previous) !== key };
      }),
    );
    if (outcome === 'invalid') {
      await this.audit.record({ event: 'email_verify_refused', playerId, ok: false, details: { why: 'replaced' } });
      throw tokenInvalid();
    }
    if (outcome === 'taken') {
      await this.audit.record({ event: 'email_verify_refused', playerId, ok: false, details: { why: 'taken', to: this.audit.addressTag(key) } });
      throw new BackendError('CONFLICT', 'Another account confirmed this address meanwhile', { reason: 'email_taken' });
    }
    await this.audit.record({ event: outcome.changed ? 'email_changed' : 'email_verified', playerId, ok: true, details: { to: this.audit.addressTag(key) } });
    return { verified: true, email: maskEmail(rec.email) };
  }

  // ------------------------------------------------------------------------------------------------ sign-in, reset

  /** auth.emailSignIn (see AuthService.signInByEmail). */
  async signIn(p: { email: string; password: string; device?: DeviceInfo }, meta?: SignInMeta): Promise<AuthOutcome<IssuedAuthResult>> {
    const addr = this.checkAddress(p.email);
    return this.auth.signInByEmail(addr.key, p.password, async () => (await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(addr.key)))?.playerId ?? null, p.device, meta);
  }

  /** auth.passwordResetRequest: { accepted: true } at once; the lookup and the mail happen afterwards. */
  async requestReset(p: { email: string; lang?: MessageLanguage }, meta?: SignInMeta): Promise<{ accepted: true }> {
    const addr = this.checkAddress(p.email);
    if (meta?.address) this.take(`c:${addressLimitKey(meta.address)}`, this.d.config.email.requestsPerClientPerHour);
    this.background(async () => {
      const holder = await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(addr.key));
      const a = holder ? await this.auth.getAccount(holder.playerId) : null;
      if (!holder || !a || a.passwordHash === null) {
        await this.audit.record({ event: 'password_reset_requested', playerId: null, ok: false, details: { to: this.audit.addressTag(addr.key), found: false } });
        return;
      }
      const sent = await this.d.mutex.run(`emailp:${a.playerId}`, async () => {
        const doc = await this.loadDoc(a.playerId);
        if (!doc.email || emailKey(doc.email) !== addr.key) return null;
        const t = this.newToken(doc, 'reset', doc.email, this.d.config.email.resetTtlMs);
        await this.d.storage.batch([...t.ops, putOp(C.emailAccounts, a.playerId, doc)]);
        return { to: doc.email, link: accountLink(this.d.config.email.linkBaseUrl!, 'reset', t.token), lang: p.lang ?? doc.lang };
      });
      if (!sent) return;
      this.deliver(a.playerId, sent.to, 'reset', sent.lang, { nickname: this.nicknameOf(a), link: sent.link, hours: Math.max(1, Math.round(this.d.config.email.resetTtlMs / HOUR_MS)) });
      await this.audit.record({ event: 'password_reset_requested', playerId: a.playerId, ok: true, details: { to: this.audit.addressTag(addr.key), found: true } });
    });
    return { accepted: true };
  }

  /**
   * auth.passwordReset: the link's account gets the new password; its sessions end; the link is used up. A pending
   * address goes with its confirmation link, and so does a Play Games link (`recovered`): the recovery leaves nothing
   * that somebody who knew the old password could still use to take the account over.
   */
  async reset(p: { token: string; password: string }): Promise<{ reset: true }> {
    const pw = checkPassword(p.password);
    if (!pw.ok) throw credentialProblemError({ field: 'password', reason: pw.reason });
    const rec = await this.findToken(p.token);
    if (!rec || rec.kind !== 'reset' || rec.expiresAt <= this.d.clock.now()) {
      if (!rec) this.audit.noteUnattributed('password_reset_refused');
      else await this.audit.record({ event: 'password_reset_refused', playerId: rec.playerId, ok: false, details: { why: rec.kind !== 'reset' ? 'wrong_kind' : 'expired' } });
      throw tokenInvalid();
    }
    const playerId = rec.playerId;
    const done = await this.d.mutex.run(`emailp:${playerId}`, async () => {
      const cur = await this.loadDoc(playerId);
      const live = cur.email !== null && emailKey(cur.email) === emailKey(rec.email) && cur.tokens.some((t) => t.hash === rec.hash);
      // Every reset link goes; a live one also takes the pending address and its confirmation links with it.
      const gone = cur.tokens.filter((t) => t.kind === 'reset' || (live && t.kind === 'verify'));
      cur.tokens = cur.tokens.filter((t) => !gone.includes(t));
      const pendingDropped = live && cur.pending !== null;
      if (live) cur.pending = null;
      await this.d.storage.batch([deleteOp(C.emailTokens, rec.hash), ...gone.map((t) => deleteOp(C.emailTokens, t.hash)), putOp(C.emailAccounts, playerId, cur)]);
      return live ? { cur, pendingDropped } : null;
    });
    if (!done) {
      await this.audit.record({ event: 'password_reset_refused', playerId, ok: false, details: { why: 'replaced' } });
      throw tokenInvalid();
    }
    const doc = done.cur;
    const ended = await this.auth.resetPassword(playerId, p.password, emailKey(doc.email!));
    const { playGamesUnlinked } = await this.recovered(playerId);
    const a = await this.auth.getAccount(playerId);
    this.deliver(playerId, doc.email!, 'password_changed', doc.lang, { nickname: this.nicknameOf(a), changeCancelled: done.pendingDropped, playGamesUnlinked });
    await this.audit.record({ event: 'password_reset', playerId, ok: true, details: { sessionsEnded: ended.length, pendingDropped: done.pendingDropped, playGamesUnlinked } });
    return { reset: true };
  }

  // ------------------------------------------------------------------------------------------------ upkeep

  /** The account's e-mail documents (account deletion): its state, its confirmed address's index entry, its links. */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const ops: StorageBatchOp[] = [];
    const doc = await this.d.storage.get<EmailAccountRecord>(C.emailAccounts, playerId);
    if (doc) {
      ops.push(deleteOp(C.emailAccounts, playerId));
      if (doc.email) {
        const held = await this.d.storage.get<EmailIndexRecord>(C.emails, emailIndexId(emailKey(doc.email)));
        if (held?.playerId === playerId) ops.push(deleteOp(C.emails, emailIndexId(emailKey(doc.email))));
      }
    }
    for (const t of await this.d.storage.list<EmailTokenRecord>(C.emailTokens)) if (t?.playerId === playerId) ops.push(deleteOp(C.emailTokens, t.hash));
    return ops;
  }

  /** Deletes the records of expired links (the sweep). Returns how many went. */
  async sweep(now: number): Promise<number> {
    const expired = (await this.d.storage.list<EmailTokenRecord>(C.emailTokens)).filter((t) => t.expiresAt <= now).map((t) => t.hash);
    for (let i = 0; i < expired.length; i += 500) await this.d.storage.batch(expired.slice(i, i + 500).map((h) => deleteOp(C.emailTokens, h)));
    return expired.length;
  }
}
